/**
 * StripFootingPage — strip footing under a continuous wall.
 *
 * Design per linear metre of wall. Left rail: inputs. Center: cross-section
 * (perpendicular to the wall) with bearing-pressure trapezoid, + 3D toggle.
 * Right rail: results.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useStripStore } from '@/features/zapatas_strip/store';
import { useStripAnalysis } from '@/features/zapatas_strip/useAnalysis';
import { useExportStrip } from '@/features/zapatas_strip/useExport';
import { Strip3DView } from '@/features/zapatas_strip/Strip3DView';
import { RhoBar } from '@/components/ui/RhoBar';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import type { RebarDiam, StripAnalyzeRequest } from '@/features/zapatas_strip/types';

const REBAR_DIAMS: RebarDiam[] = ['3/8', '1/2', '5/8', '3/4', '1', '1 3/8'];

export function StripFootingPage({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const request = useStripStore((s) => s.request);
  const setSection = useStripStore((s) => s.setSection);
  const setMaterials = useStripStore((s) => s.setMaterials);
  const setSoil = useStripStore((s) => s.setSoil);
  const setLoads = useStripStore((s) => s.setLoads);
  const setReinforcement = useStripStore((s) => s.setReinforcement);
  const loadRequest = useStripStore((s) => s.loadRequest);

  // URL hydration: /app/footings/:id loads a saved design IF its subtype is "strip".
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data && designQuery.data.subtype === 'strip') {
      loadRequest(designQuery.data.payload as unknown as StripAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const [saveOpen, setSaveOpen] = useState(false);
  const { data, isFetching, isError, error } = useStripAnalysis(request);
  const exporter = useExportStrip();
  const [view, setView] = useState<'2D' | '3D'>('2D');

  const matchingDesign =
    designQuery.data && designQuery.data.subtype === 'strip' ? designQuery.data : null;

  return (
    <div className="flex h-full flex-col">
      {!hideHeader && (
        <header className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}>
          <Link to="/app/footings" className="flex items-center gap-1 text-[11px]"
            style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={14} />
            <span>Hub zapatas</span>
          </Link>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Software E.060 + E.050
            </div>
            <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
              Diseño de zapata corrida (bajo muro)
            </div>
          </div>
        </header>
      )}

      <DesignToolbar
        status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
        statusLabel={isError ? `Error: ${String(error)}` : undefined}
        onExportPdf={() => exporter.trigger('pdf', request)}
        onExportXlsx={() => exporter.trigger('xlsx', request)}
        busyExport={exporter.busy}
        onSave={() => setSaveOpen(true)}
        isUpdate={!!matchingDesign}
      />
      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="zapatas"
        subtype="strip"
        payload={request as unknown as Record<string, unknown>}
        existing={matchingDesign}
      />

      <div className="flex flex-1 overflow-hidden">
        <aside className="flex w-[300px] shrink-0 flex-col gap-3 overflow-y-auto border-r p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
          <Group title="Geometría (sección)">
            <NumberRow label="B (cm) — ancho zapata" value={request.section.B} onChange={(v) => setSection({ B: v })} step={5} />
            <NumberRow label="Hf (cm)" value={request.section.Hf} onChange={(v) => setSection({ Hf: v })} step={5} />
            <NumberRow label="tw (cm) — muro" value={request.section.tw} onChange={(v) => setSection({ tw: v })} step={1} />
            <NumberRow label="df (cm)" value={request.section.df} onChange={(v) => setSection({ df: v })} step={10} />
          </Group>

          <Group title="Materiales">
            <NumberRow label="f'c (kgf/cm²)" value={request.materials.fc} onChange={(v) => setMaterials({ fc: v })} step={10} />
            <NumberRow label="fy (kgf/cm²)" value={request.materials.fy} onChange={(v) => setMaterials({ fy: v })} step={100} />
            <NumberRow label="Recubrim. (cm)" value={request.materials.cover} onChange={(v) => setMaterials({ cover: v })} step={0.5} />
          </Group>

          <Group title="Suelo (E.050)">
            <NumberRow label="σ_adm (kgf/cm²)" value={request.soil.sigma_adm} onChange={(v) => setSoil({ sigma_adm: v })} step={0.1} />
            <NumberRow label="γ_s (kgf/m³)" value={request.soil.gamma_s} onChange={(v) => setSoil({ gamma_s: v })} step={50} />
            <NumberRow label="γ_c (kgf/m³)" value={request.soil.gamma_c} onChange={(v) => setSoil({ gamma_c: v })} step={50} />
            <NumberRow label="Factor σ_adm" value={request.soil.sigma_factor} onChange={(v) => setSoil({ sigma_factor: v })} step={0.05} />
          </Group>

          <Group title="Cargas por metro de muro">
            <NumberRow label="P servicio (kgf/m)" value={request.loads.P_per_m} onChange={(v) => setLoads({ P_per_m: v })} step={500} />
            <NumberRow label="M servicio (kgf·cm/m)" value={request.loads.M_per_m} onChange={(v) => setLoads({ M_per_m: v })} step={10000} />
            <NumberRow label="Pu factor (kgf/m)" value={request.loads.Pu_per_m} onChange={(v) => setLoads({ Pu_per_m: v })} step={500} />
            <NumberRow label="Mu factor (kgf·cm/m)" value={request.loads.Mu_per_m} onChange={(v) => setLoads({ Mu_per_m: v })} step={10000} />
          </Group>

          <Group title="Refuerzo principal (transversal)">
            <SelectRow label="Diámetro" value={request.reinforcement.diam_main}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_main: v as RebarDiam })} />
            <NumberRow label="Espaciamiento (cm)" value={request.reinforcement.s_main}
              onChange={(v) => setReinforcement({ s_main: v })} step={1} />
          </Group>

          <Group title="Refuerzo de distribución (long.)">
            <SelectRow label="Diámetro" value={request.reinforcement.diam_dist}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_dist: v as RebarDiam })} />
            <NumberRow label="Espaciamiento (cm)" value={request.reinforcement.s_dist}
              onChange={(v) => setReinforcement({ s_dist: v })} step={1} />
          </Group>
        </aside>

        <main className="flex-1 overflow-auto p-4" style={{ background: 'var(--surface-canvas)' }}>
          {data && (
            <div className="flex flex-col gap-4">
              <DiagramCard
                title={view === '3D' ? 'Vista 3D — tramo de zapata corrida' : 'Sección transversal (perpendicular al muro)'}
                toolbar={
                  <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
                    {(['2D', '3D'] as const).map((m) => (
                      <button key={m} type="button" onClick={() => setView(m)}
                        className="h-7 px-3 text-[10px] font-semibold tracking-[0.12em]"
                        style={{ background: view === m ? 'var(--accent)' : 'transparent',
                          color: view === m ? 'var(--surface-base)' : 'var(--text-secondary)' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                }
              >
                {view === '3D' ? (
                  <div style={{ width: 880, height: 480 }}>
                    <Strip3DView
                      B={request.section.B} Hf={request.section.Hf}
                      tw={request.section.tw} df={request.section.df}
                      cover={request.materials.cover}
                      diam_main={request.reinforcement.diam_main}
                      s_main={request.reinforcement.s_main}
                      diam_dist={request.reinforcement.diam_dist}
                      s_dist={request.reinforcement.s_dist}
                    />
                  </div>
                ) : (
                  <StripCrossSection
                    B={request.section.B} Hf={request.section.Hf}
                    tw={request.section.tw} df={request.section.df}
                    cover={request.materials.cover}
                    diam_main={request.reinforcement.diam_main}
                    s_main={request.reinforcement.s_main}
                    sigma_max={data.bearing.sigma_max}
                    sigma_min={data.bearing.sigma_min}
                    sigma_adm={data.bearing.sigma_adm_eff}
                  />
                )}
              </DiagramCard>

              {view === '2D' && (
                <DiagramCard title="Vista en planta — tramo de zapata corrida">
                  <StripPlanDiagram
                    B={request.section.B}
                    tw={request.section.tw}
                    s_main={request.reinforcement.s_main}
                    s_dist={request.reinforcement.s_dist}
                    diam_main={request.reinforcement.diam_main}
                    diam_dist={request.reinforcement.diam_dist}
                  />
                </DiagramCard>
              )}
            </div>
          )}
        </main>

        <aside className="flex w-[340px] shrink-0 flex-col gap-3 overflow-y-auto border-l p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
          {data && (
            <>
              <ResultCard title="Geometría (por metro)">
                <KV label="Área por m" value={`${(data.geometry.A_per_m / 100).toFixed(1)} cm × 100 cm`} />
                <KV label="Peso propio" value={`${data.geometry.self_weight_per_m.toFixed(0)} kgf/m`} />
                <KV label="Peso suelo" value={`${data.geometry.soil_above_per_m.toFixed(0)} kgf/m`} />
                <KV label="d efectivo" value={`${data.geometry.d_effective.toFixed(1)} cm`} />
                <KV label="Voladizo (B-tw)/2" value={`${data.geometry.cantilever.toFixed(1)} cm`} />
              </ResultCard>

              <ResultCard title="Presión sobre el suelo">
                <KV label="P_total / m" value={`${(data.bearing.P_total_per_m / 1000).toFixed(2)} tonf/m`} />
                <KV label="Excentricidad" value={`${data.bearing.e.toFixed(2)} cm`} />
                <KV label="σ_max" value={`${data.bearing.sigma_max.toFixed(3)} kgf/cm² (${(data.bearing.sigma_max * 10).toFixed(2)} t/m²)`} mono />
                <KV label="σ_min" value={`${data.bearing.sigma_min.toFixed(3)} kgf/cm²`} mono />
                <KV label="σ_adm" value={`${data.bearing.sigma_adm_eff.toFixed(3)} kgf/cm²`} />
                <div className="mt-1 text-[10px]" style={{ color: data.bearing.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.bearing.ok ? '✓ ' : '✗ '}{data.bearing.note}
                </div>
              </ResultCard>

              <ResultCard title="Cortante 1D">
                <KV label="x crítico" value={`${data.one_way_shear.critical_distance.toFixed(1)} cm`} />
                <KV label="Vu" value={`${(data.one_way_shear.Vu_per_m / 1000).toFixed(2)} tonf/m`} />
                <KV label="φVc" value={`${(data.one_way_shear.phi_Vc_per_m / 1000).toFixed(2)} tonf/m`} />
                <div className="mt-1 text-[10px]" style={{ color: data.one_way_shear.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.one_way_shear.ok ? '✓ OK' : '✗ Aumentar Hf'}
                </div>
              </ResultCard>

              <ResultCard title="Refuerzo principal (transversal)">
                <KV label="Mu" value={`${(data.flexion.Mu_per_m / 1e5).toFixed(3)} tonf·m/m`} />
                <KV label="As req." value={`${data.flexion.As_required_per_m.toFixed(2)} cm²/m`} />
                <KV label="As prov." value={`${data.flexion.As_provided_per_m.toFixed(2)} cm²/m`} />
                <KV label="As_min" value={`${data.flexion.As_min_per_m.toFixed(2)} cm²/m`} />
                <KV label="Configuración" value={data.flexion.label} mono />
                <div className="mt-2">
                  <RhoBar
                    title="ρ principal — As / (100 · d)"
                    rho={data.flexion.rho}
                    rho_min={data.flexion.rho_min}
                    rho_max={0.04}
                    hideAdvice
                  />
                </div>
                <div className="mt-1 text-[10px]" style={{ color: data.flexion.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.flexion.ok ? '✓ OK' : '✗ Densificar o subir diámetro'}
                </div>
              </ResultCard>

              <ResultCard title="Refuerzo de distribución (longitudinal)">
                <KV label="As_min" value={`${data.distribution.As_min_per_m.toFixed(2)} cm²/m`} />
                <KV label="As prov." value={`${data.distribution.As_provided_per_m.toFixed(2)} cm²/m`} />
                <KV label="Configuración" value={data.distribution.label} mono />
                <div className="mt-1 text-[10px]" style={{ color: data.distribution.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.distribution.ok ? '✓ OK' : '✗ Cuantía mínima no se cumple'}
                </div>
              </ResultCard>

              <ResultCard title="Verificaciones">
                {data.verifications.map((v) => (
                  <div key={v.id} className="flex items-start gap-2 py-0.5">
                    <span className="mt-0.5" style={{ color: v.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700, minWidth: 12 }}>
                      {v.ok ? '✓' : '✗'}
                    </span>
                    <span className="flex-1 text-[10px]" style={{ lineHeight: 1.35 }}>{v.label}</span>
                  </div>
                ))}
              </ResultCard>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function NumberRow({ label, value, onChange, step = 1 }: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input type="number" value={value} step={step}
        onChange={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v)) onChange(v); }}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }} />
    </label>
  );
}
function SelectRow({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-[11px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function ResultCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {children}
    </div>
  );
}
function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className={mono ? 'font-mono text-[10.5px]' : 'text-[10.5px]'} style={{ color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
function DiagramCard({ title, children, toolbar }: { title: string; children: React.ReactNode; toolbar?: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-md)] border p-3" style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/* ====================================================================== */
/*  STRIP CROSS-SECTION DIAGRAM                                             */
/* ====================================================================== */

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525, '1/2': 1.27, '5/8': 1.5875, '3/4': 1.905, '1': 2.54, '1 3/8': 3.4925,
};

function StripCrossSection({
  B, Hf, tw, df, cover, diam_main, s_main, sigma_max, sigma_min, sigma_adm,
}: {
  B: number; Hf: number; tw: number; df: number; cover: number;
  diam_main: string; s_main: number;
  sigma_max: number; sigma_min: number; sigma_adm: number;
}) {
  const W = 880;
  const totalH = df + 80;
  const PAD = 50;
  const drawW = W - 2 * PAD;
  const scaleX = drawW / B;
  const scaleY = Math.min(scaleX, 240 / Math.max(totalH, 1));
  const wpx = B * scaleX;
  const xL = (W - wpx) / 2;
  const yGrade = 50;
  const ySlabTop = yGrade + (df - Hf) * scaleY;
  const ySlabBot = ySlabTop + Hf * scaleY;
  const H = ySlabBot + 140;
  const twpx = tw * scaleX;
  const colX = xL + (wpx - twpx) / 2;
  const wallTop = yGrade - 60;

  const d_main = DIAM_INCH_TO_CM[diam_main] ?? 1.27;
  const yMain = ySlabBot - (cover + d_main / 2) * scaleY;
  // Main bars distributed across B (transverse, dots in cross-section)
  const nMain = Math.max(2, Math.floor(B / s_main));
  const xs_main: number[] = [];
  for (let i = 0; i < nMain; i++) {
    const xCm = -B / 2 + cover + d_main / 2 + (i * (B - 2 * (cover + d_main / 2))) / (nMain - 1);
    xs_main.push((xCm + B / 2) * scaleX + xL);
  }

  // Stress trapezoid below slab
  const sRef = Math.max(sigma_max, sigma_adm, 1e-6);
  const yStress = ySlabBot + 22;
  const smaxPx = (sigma_max / sRef) * 60;
  const sminPx = Math.max(sigma_min, 0) / sRef * 60;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-strip" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
        <pattern id="soil-strip" patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#7C2D12" fillOpacity="0.10" />
          <circle cx="2" cy="2" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
          <circle cx="6" cy="6" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
        </pattern>
        <pattern id="brick" patternUnits="userSpaceOnUse" width="14" height="8">
          <rect width="14" height="8" fill="#A16207" fillOpacity="0.45" />
          <line x1="0" y1="4" x2="14" y2="4" stroke="#451A03" strokeWidth="0.6" />
          <line x1="7" y1="0" x2="7" y2="4" stroke="#451A03" strokeWidth="0.6" />
          <line x1="0" y1="4" x2="0" y2="8" stroke="#451A03" strokeWidth="0.6" />
          <line x1="14" y1="4" x2="14" y2="8" stroke="#451A03" strokeWidth="0.6" />
        </pattern>
      </defs>

      {/* Soil */}
      <rect x={xL - 60} y={yGrade} width={wpx + 120} height={ySlabBot - yGrade + 10} fill="url(#soil-strip)" />
      <line x1={xL - 60} y1={yGrade} x2={xL + wpx + 60} y2={yGrade} stroke="#7C2D12" strokeWidth="1.4" />
      <text x={xL - 60} y={yGrade - 4} fontSize="10" fontFamily="monospace" fill="#7C2D12">NPT (grade)</text>

      {/* Footing */}
      <rect x={xL} y={ySlabTop} width={wpx} height={Hf * scaleY}
        fill="url(#hatch-strip)" stroke="var(--concrete-outline)" strokeWidth="1.4" />

      {/* Wall (brick) */}
      <rect x={colX} y={wallTop} width={twpx} height={ySlabTop - wallTop}
        fill="url(#brick)" stroke="#451A03" strokeWidth="1.2" />

      {/* Main bars (dots) */}
      {xs_main.map((x, i) => (
        <circle key={`bar-${i}`} cx={x} cy={yMain} r={4} fill="var(--accent)"
          stroke="var(--concrete-outline)" strokeWidth="0.6" />
      ))}

      {/* Stress trapezoid (sigma_max under one edge, sigma_min under the other) */}
      <polygon
        points={`${xL},${yStress} ${xL + wpx},${yStress} ${xL + wpx},${yStress - sminPx} ${xL},${yStress - smaxPx}`}
        fill="rgba(220,38,38,0.18)" stroke="var(--danger)" strokeWidth="1.3"
      />
      <text x={xL - 4} y={yStress - smaxPx - 4} textAnchor="end" fontSize="11" fontFamily="monospace"
        fill="var(--danger)" style={{ fontWeight: 700 }}>σ_max = {sigma_max.toFixed(3)} kgf/cm²</text>
      <text x={xL + wpx + 4} y={yStress - sminPx - 4} fontSize="11" fontFamily="monospace"
        fill="var(--danger)" style={{ fontWeight: 700 }}>σ_min = {sigma_min.toFixed(3)}</text>

      {/* sigma_adm reference */}
      {(() => {
        const padmPx = (sigma_adm / sRef) * 60;
        return (
          <g>
            <line x1={xL} y1={yStress - padmPx} x2={xL + wpx} y2={yStress - padmPx}
              stroke="var(--success)" strokeWidth="1.0" strokeDasharray="4 3" />
            <text x={xL + wpx + 4} y={yStress - padmPx + 3} fontSize="10" fontFamily="monospace" fill="var(--success)">
              σ_adm = {sigma_adm.toFixed(3)}
            </text>
          </g>
        );
      })()}

      <text x={W / 2} y={ySlabBot + 12} textAnchor="middle" fontSize="12" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        B = {B} cm  ·  Hf = {Hf} cm  ·  tw = {tw} cm  ·  df = {df} cm
      </text>

      {/* Legend */}
      <g transform={`translate(${PAD}, ${H - 16})`}>
        <circle cx="6" cy="0" r="4" fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.6" />
        <text x="16" y="3" fontSize="10" fontFamily="monospace" fill="var(--text-secondary)">
          {`acero principal Ø${diam_main}" @ ${s_main.toFixed(0)} cm`}
        </text>
      </g>
    </svg>
  );
}

/* ====================================================================== */
/*  PLAN VIEW — tramo de zapata corrida bajo el muro                        */
/* ====================================================================== */

function StripPlanDiagram({
  B, tw, s_main, s_dist, diam_main, diam_dist,
}: {
  B: number; tw: number; s_main: number; s_dist: number;
  diam_main: string; diam_dist: string;
}) {
  const W = 880;
  const L_show = Math.max(300, 4 * s_main);   // ~3 m or 4 main-bar spacings
  const PAD = 50;
  const drawW = W - 2 * PAD;
  const scale = Math.min(drawW / L_show, 200 / B);
  const wpx = L_show * scale;
  const hpx = B * scale;
  const xL = (W - wpx) / 2;
  const yT = 36;
  const yB = yT + hpx;
  const H = yB + 70;

  // Wall (centered along B axis, runs along the L direction)
  const twpx = tw * scale;
  const wallY = yT + (hpx - twpx) / 2;

  // Main bars (transverse to the wall) — vertical lines in this plan view
  const nMain = Math.max(2, Math.floor(L_show / s_main));
  const mainStartX = xL + (L_show - (nMain - 1) * s_main) / 2 * scale;
  // Distribution bars (along the wall) — horizontal dashed lines
  const nDist = Math.max(2, Math.floor(B / s_dist));
  const distStartY = yT + (B - (nDist - 1) * s_dist) / 2 * scale;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-strip-plan" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.18" strokeWidth="1" />
        </pattern>
        <pattern id="brick-plan" patternUnits="userSpaceOnUse" width="14" height="8">
          <rect width="14" height="8" fill="#A16207" fillOpacity="0.40" />
          <line x1="0" y1="4" x2="14" y2="4" stroke="#451A03" strokeWidth="0.5" />
          <line x1="7" y1="0" x2="7" y2="4" stroke="#451A03" strokeWidth="0.5" />
        </pattern>
      </defs>

      {/* Footing slab outline */}
      <rect x={xL} y={yT} width={wpx} height={hpx}
        fill="url(#hatch-strip-plan)" stroke="var(--concrete-outline)" strokeWidth="1.4" />

      {/* Wall (continuous along the length, shown as a brick-pattern strip) */}
      <rect x={xL} y={wallY} width={wpx} height={twpx}
        fill="url(#brick-plan)" stroke="#451A03" strokeWidth="1.2" />
      <text x={xL + wpx / 2} y={wallY + twpx / 2 + 3} textAnchor="middle"
        fontSize="10" fontFamily="monospace" fill="#451A03" style={{ fontWeight: 700 }}>
        MURO (tw = {tw} cm)
      </text>

      {/* Distribution bars (longitudinal, along the wall) — dashed violet */}
      {Array.from({ length: nDist }).map((_, i) => {
        const y = distStartY + i * s_dist * scale;
        return (
          <line key={`d-${i}`}
            x1={xL + 4} y1={y} x2={xL + wpx - 4} y2={y}
            stroke="var(--brand-violet, #8B5CF6)" strokeWidth="1.2" strokeDasharray="3 2" />
        );
      })}
      {/* Main bars (transverse, across the wall) — solid amber */}
      {Array.from({ length: nMain }).map((_, i) => {
        const x = mainStartX + i * s_main * scale;
        return (
          <line key={`m-${i}`}
            x1={x} y1={yT + 4} x2={x} y2={yB - 4}
            stroke="var(--accent)" strokeWidth="1.6" />
        );
      })}

      {/* Continuity arrows — strip continues past both edges */}
      {[xL, xL + wpx].map((x, i) => (
        <g key={`cont-${i}`} transform={`translate(${i === 0 ? x - 30 : x + 30}, ${yT + hpx / 2})`}>
          <line x1="-12" y1="0" x2="12" y2="0" stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3 2" />
        </g>
      ))}

      {/* Dimensions */}
      <text x={W / 2} y={yB + 22} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        Tramo mostrado: {L_show} cm  ·  B = {B} cm  ·  tw = {tw} cm
      </text>

      {/* Legend */}
      <g transform={`translate(${PAD}, ${yB + 42})`}>
        <line x1="0" y1="0" x2="18" y2="0" stroke="var(--accent)" strokeWidth="1.6" />
        <text x="22" y="3" fontSize="10" fontFamily="monospace" fill="var(--accent)">
          {`acero principal Ø${diam_main}" @ ${s_main} cm`}
        </text>
        <line x1="270" y1="0" x2="288" y2="0" stroke="var(--brand-violet, #8B5CF6)" strokeWidth="1.4" strokeDasharray="3 2" />
        <text x="292" y="3" fontSize="10" fontFamily="monospace" fill="var(--brand-violet, #8B5CF6)">
          {`distribución Ø${diam_dist}" @ ${s_dist} cm`}
        </text>
        <rect x="510" y="-4" width="12" height="8" fill="url(#brick-plan)" stroke="#451A03" strokeWidth="0.5" />
        <text x="528" y="3" fontSize="10" fontFamily="monospace" fill="#451A03">muro</text>
      </g>
    </svg>
  );
}
